import React, { useState } from "react";

import { Instant } from "@js-joda/core";
import { DashboardLayoutType } from "api/model/dashboard/DashboardComponent";
import { DashboardComponentGroup } from "api/model/dashboard/DashboardComponentGroup";
import { DashboardTabModel } from "api/model/dashboard/DashboardTab";
import { useDisplayTimezone } from "hooks/useTimezone";
import { Button } from "primereact/button";
import { Column, ColumnEditorOptions } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import {
  DataTable,
  DataTableRowEditCompleteEvent,
  DataTableRowExpansionTemplate,
  DataTableRowReorderEvent,
} from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { fmtDatetime } from "utils/time-utils";
import GroupSubTableEditor from "./components/GroupSubTableEditor";
import "./DashboardReportTabEditor.scss";

const DashboardReportTabEditor: React.FC<{
  dashboardTabs: Array<Partial<DashboardTabModel>>;
  onChange: (o: Array<Partial<DashboardTabModel>>) => void;
}> = ({ dashboardTabs, onChange }) => {
  const displayTz = useDisplayTimezone();
  const [expandedTabRows, setExpandedTabRows] = useState<Partial<DashboardTabModel>[]>([...dashboardTabs]);

  const onRowEditComplete = (e: DataTableRowEditCompleteEvent) => {
    replaceTab(e.newData, e.index);
  };

  const textEditor = (options: ColumnEditorOptions) => {
    return (
      <InputText type="text" value={options.value} onChange={e => options.editorCallback!!(e.target.value)} />
    );
  };

  const replaceTab = (givenTab: Partial<DashboardTabModel>, index: number) => {
    onChange(dashboardTabs.map((dt, idx) => (idx === index ? givenTab : dt)));

    setExpandedTabRows(oldExpands => {
      if (!Array.isArray(oldExpands)) {
        return dashboardTabs;
      }

      if (!givenTab.componentGroups || (givenTab.componentGroups && givenTab.componentGroups.length === 0)) {
        return oldExpands.filter(dashboard => dashboard !== givenTab);
      }

      if (!oldExpands.some(dashboard => dashboard === givenTab)) {
        return [...oldExpands, givenTab];
      }

      return oldExpands.map(dashboard => (dashboard === givenTab ? givenTab : dashboard));
    });
  };

  const onAddTabClick = () => {
    const tab: Partial<DashboardTabModel> = {
      title: fmtDatetime(Instant.now(), displayTz),
      componentGroups: [],
    };

    onChange([...dashboardTabs, tab]);
  };

  const onRemoveTabClick = (rowData: DashboardTabModel) => {
    onChange(dashboardTabs.filter(el => el !== rowData));
  };

  const onRowReorder = (e: DataTableRowReorderEvent<DashboardTabModel[]>) => {
    onChange(e.value);
  };

  const confirmTabDelete = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    rowData: DashboardTabModel
  ) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-popup.do-you-want-to-delete-this-tab"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: () => onRemoveTabClick(rowData),
    });
  };

  const onAddGroupClick = (rowData: DashboardTabModel) => {
    const tempTab: DashboardTabModel = {
      ...rowData,
      componentGroups: [
        ...rowData.componentGroups,
        {
          components: [],
          layout: { type: DashboardLayoutType.VERTICAL },
        } as unknown as DashboardComponentGroup,
      ],
    };

    onChange(dashboardTabs.map(d => (d === rowData ? tempTab : d)));

    setExpandedTabRows(oldExpands => {
      if (!Array.isArray(oldExpands)) {
        return [tempTab];
      }

      if (!oldExpands.some(dashboard => dashboard === rowData)) {
        return [...oldExpands, tempTab];
      }

      return oldExpands.map(dashboard => (dashboard === rowData ? tempTab : dashboard));
    });
  };

  const addButton = (rowData: DashboardTabModel) => (
    <Button
      icon="pi pi-plus"
      label={T("word.group")}
      className="p-button-sm p-button-success"
      onClick={() => {
        onAddGroupClick(rowData);
      }}
    />
  );
  const removeButton = (rowData: DashboardTabModel) => (
    <Button
      icon="pi pi-trash"
      className="p-button-sm p-button-danger"
      onClick={event => confirmTabDelete(event, rowData)}
    />
  );

  const renderExpansionTemplate = (data: DashboardTabModel, options: DataTableRowExpansionTemplate) => {
    return (
      <GroupSubTableEditor key={options.index} tab={data} onChange={replaceTab} tabIndex={options.index} />
    );
  };

  return (
    <div style={{ height: "500px", overflowY: "auto" }} className="flex flex-column gap-1">
      <Button
        icon="pi pi-plus"
        label={T("word.tab")}
        className="mt-2 p-button-success p-button-sm"
        onClick={() => onAddTabClick()}
      />
      <DataTable
        value={dashboardTabs}
        size="small"
        reorderableRows
        onRowReorder={onRowReorder}
        editMode="row"
        expandedRows={expandedTabRows}
        onRowEditComplete={onRowEditComplete}
        onRowToggle={e => setExpandedTabRows(e.data as Partial<DashboardTabModel>[])}
        rowExpansionTemplate={renderExpansionTemplate}
      >
        <Column expander={rowData => rowData.componentGroups?.length > 0} style={{ width: "3em" }} />
        <Column field="title" header={T("word.tabs")} editor={options => textEditor(options)} />
        <Column
          rowEditor
          headerStyle={{ width: "0", minWidth: "6rem" }}
          bodyStyle={{ textAlign: "center" }}
        />
        <Column style={{ width: "0" }} body={addButton} />
        <Column style={{ width: "0" }} body={removeButton} />
        <Column rowReorder style={{ width: "3em" }} />
      </DataTable>
    </div>
  );
};

export default DashboardReportTabEditor;
